export type ParsedChangelogPreviewItem = {
  title: string;
  details: string[];
  /** Estilo compacto: "Título — descrição" na mesma linha */
  inlineDescription?: string;
};

const BLOCK_TAGS = new Set(['P', 'DIV']);

function pushItem(
  items: ParsedChangelogPreviewItem[],
  title: string,
  details: string[],
  inlineDescription?: string
) {
  if (details.length > 0) {
    items.push({ title, details });
  } else if (inlineDescription) {
    items.push({ title, details: [], inlineDescription });
  } else if (title) {
    items.push({ title, details: [] });
  }
}

/**
 * Extrai itens do HTML gerado pelo editor (mesmo formato que `changelogItemsToHtml`).
 * Só usar em cliente (usa DOMParser).
 * Aceita `<p>` e `<div>` (contentEditable em Chrome costuma usar div).
 */
export function parseChangelogBlockHtml(html: string): ParsedChangelogPreviewItem[] {
  if (typeof DOMParser === 'undefined') return [];
  const wrap = html?.trim() ? `<div data-root>${html}</div>` : '<div data-root><p></p></div>';
  const doc = new DOMParser().parseFromString(wrap, 'text/html');
  const root = doc.querySelector('[data-root]');
  if (!root) return [];

  const items: ParsedChangelogPreviewItem[] = [];
  const children = Array.from(root.children);

  for (let i = 0; i < children.length; i++) {
    const el = children[i];

    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const details: string[] = [];
      el.querySelectorAll(':scope > li').forEach((li) => {
        const t = li.textContent?.trim();
        if (t) details.push(t);
      });
      if (details.length === 1) {
        pushItem(items, details[0], []);
      } else if (details.length > 1) {
        pushItem(items, details[0], details.slice(1));
      }
      continue;
    }

    if (!BLOCK_TAGS.has(el.tagName)) continue;

    const block = el as HTMLElement;
    const strong = block.querySelector('strong, b');
    const titleFromStrong = strong?.textContent?.trim() ?? '';
    const fullText = block.textContent?.trim() ?? '';

    let inlineDescription: string | undefined;
    if (strong && titleFromStrong) {
      const idx = fullText.indexOf(titleFromStrong);
      if (idx >= 0) {
        let after = fullText.slice(idx + titleFromStrong.length).trim();
        after = after.replace(/^[\s:]*[—–-]\s*/, '').trim();
        if (after) inlineDescription = after;
      }
    }

    const details: string[] = [];
    const next = children[i + 1];
    if (next?.tagName === 'UL' || next?.tagName === 'OL') {
      next.querySelectorAll(':scope > li').forEach((li) => {
        const t = li.textContent?.trim();
        if (t) details.push(t);
      });
      i++;
    }

    const title = titleFromStrong || fullText;
    if (!title && details.length === 0) continue;
    pushItem(items, title || 'Item', details, inlineDescription);
  }

  // Texto solto no root (sem blocos), ex.: menção colada sem Enter
  if (items.length === 0) {
    const loose = root.textContent?.trim();
    if (loose) pushItem(items, loose, []);
  }

  return items;
}
